// Build flags are now separated into two concepts:
// - The "role" (students, teachers, admin, landing) that drives UI branches.
// - The "demo" flag, which can be true alongside any role.

const ROLE_STORAGE_KEY = 'pi-schedule-demo-role';
const ROLE_CHANGE_EVENT = 'pi-schedule-build-role-change';
const ALLOWED_ROLES = ['STUDENTS', 'TEACHERS', 'ADMIN', 'LANDING'];

const normalizeRole = (value) => {
  const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return ALLOWED_ROLES.includes(normalized) ? normalized : null;
};

const resolveBaseRole = () => {
  const raw = process.env.REACT_APP_TYPE;
  const normalized = typeof raw === 'string' ? raw.trim().toUpperCase() : '';

  // Legacy "DEMO" type now maps to ADMIN role + demo flag.
  if (normalized === 'DEMO') {
    return 'ADMIN';
  }

  return normalizeRole(normalized) || 'ADMIN';
};

const resolveDemoFlag = () => {
  const raw = process.env.REACT_APP_TYPE;
  const normalized = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  if (normalized === 'DEMO') return true;

  const demoEnv = process.env.REACT_APP_DEMO;
  if (typeof demoEnv === 'string') {
    return demoEnv.trim().toLowerCase() === 'true';
  }
  return !!demoEnv;
};

const readStoredRole = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(ROLE_STORAGE_KEY);
    return normalizeRole(stored);
  } catch (err) {
    console.warn('[AppStatic] Failed to read stored demo role override:', err);
    return null;
  }
};

const persistRole = (role) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ROLE_STORAGE_KEY, role);
  } catch (err) {
    console.warn('[AppStatic] Failed to persist demo role override:', err);
  }
};

const BASE_ROLE = resolveBaseRole();
let demoMode = resolveDemoFlag();
let currentRole = demoMode ? readStoredRole() || BASE_ROLE : BASE_ROLE;

export const getBuildType = () => currentRole;
export const getBuildRole = getBuildType;
export const isStudentsBuild = () => currentRole === 'STUDENTS';
export const isTeachersBuild = () => currentRole === 'TEACHERS';
export const isAdminBuild = () => currentRole === 'ADMIN';
export const isLandingBuild = () => currentRole === 'LANDING';
export const isDemoBuild = () => demoMode;
// Teachers/Students apps are public-facing: ignore admin login sessions
// (e.g. Firebase auth left over from the admin app on the same domain).
export const isPublicFacingBuild = () => currentRole === 'STUDENTS' || currentRole === 'TEACHERS';
export const getPublicFacingUser = () => ({
  uid: null,
  email: null,
  role: currentRole === 'TEACHERS' ? 'Teacher' : 'Student',
  token: null
});
export const BUILD_ROLE_CHANGE_EVENT = ROLE_CHANGE_EVENT;

export const setBuildRole = (role) => {
  const normalized = normalizeRole(role);
  if (!normalized) return currentRole;
  currentRole = normalized;
  if (demoMode) {
    persistRole(normalized);
  }
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(ROLE_CHANGE_EVENT, { detail: { role: currentRole } }));
    } catch (err) {
      // Ignore environments without CustomEvent
    }
  }
  return currentRole;
};

export const setDemoMode = (flag) => {
  const nextDemoMode = !!flag;
  if (nextDemoMode === demoMode) return demoMode;

  demoMode = nextDemoMode;

  if (!demoMode) {
    currentRole = BASE_ROLE; // outside demo, always honor build-time role
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(ROLE_STORAGE_KEY);
      } catch (err) {
        console.warn('[AppStatic] Failed to clear demo role override:', err);
      }
    }
    return demoMode;
  }

  const storedRole = readStoredRole();
  if (storedRole) {
    currentRole = storedRole;
  }
  return demoMode;
};

const AppStatic = {
  getBuildType,
  getBuildRole,
  isStudentsBuild,
  isTeachersBuild,
  isAdminBuild,
  isDemoBuild,
  isLandingBuild,
  isPublicFacingBuild,
  getPublicFacingUser,
  setBuildRole,
  setDemoMode
};

export default AppStatic;